import { useEffect, useState } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { STATUS, ConfBadge } from "./shared";

export function DuplicatesDialog({ open, onClose, onResolved }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      api.get("/invoices/duplicates").then((r) => setGroups(r.data)).finally(() => setLoading(false));
    }
  }, [open]);

  const keep = async (group, keepInv) => {
    const remove = group.invoices.filter((i) => i.id !== keepInv.id).map((i) => i.id);
    try {
      await api.post("/invoices/resolve-duplicate", { keep_id: keepInv.id, remove_ids: remove });
      toast.success(`Conservada ${keepInv.invoice_number || keepInv.file_name} — ${remove.length} eliminada(s)`);
      setGroups(groups.filter((gr) => gr.key !== group.key));
      onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al resolver duplicado");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="duplicates-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Facturas duplicadas ({groups.length} grupos)</DialogTitle>
        </DialogHeader>
        {loading && <div className="py-10 text-center text-sm text-slate-400">Buscando duplicados...</div>}
        {!loading && groups.length === 0 && (
          <div className="py-10 text-center">
            <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="text-sm text-slate-500">No hay grupos de duplicados pendientes</div>
          </div>
        )}
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.key} className="border rounded-xl p-4" data-testid={`dup-group-${g.key.slice(0, 8)}`}>
              <div className="text-xs text-slate-500 mb-3 uppercase tracking-wider">
                Posibles duplicados (CIF/nombre + nº + total) — elige cuál conservar
              </div>
              {(g.date_differs || g.weak_match) && (
                <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
                  ⚠ {g.weak_match ? "Coincidencia débil (OCR sin CIF o nº en alguna factura). " : ""}
                  {g.date_differs ? "Las fechas de factura difieren — podrían ser compras distintas. Revisa los originales antes de eliminar." : ""}
                </div>
              )}
              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${g.invoices.length}, minmax(0, 1fr))` }}>
                {g.invoices.map((inv) => (
                  <div key={inv.id} className="border rounded-lg p-4 space-y-2 bg-slate-50" data-testid={`dup-card-${inv.id}`}>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className={(STATUS[inv.status] || {}).cls}>{(STATUS[inv.status] || {}).label}</Badge>
                      <ConfBadge value={inv.overall_confidence} />
                    </div>
                    <div className="text-sm font-medium truncate" title={inv.file_name}>{inv.file_name}</div>
                    <div className="space-y-1 text-xs text-slate-600">
                      <div className="flex justify-between"><span>Proveedor</span><span className="font-medium">{inv.supplier_name || "—"}</span></div>
                      <div className="flex justify-between"><span>Nº factura</span><span className="font-num">{inv.invoice_number || "—"}</span></div>
                      <div className="flex justify-between"><span>Fecha</span><span>{fmtDate(inv.invoice_date)}</span></div>
                      <div className="flex justify-between"><span>Total</span><span className="font-num font-semibold">{fmtEUR(inv.total)}</span></div>
                      <div className="flex justify-between"><span>Fuente</span><span>{inv.source === "google_drive" ? `Drive · ${inv.drive_folder || ""}` : "Subida manual"}</span></div>
                      <div className="flex justify-between"><span>Detectada</span><span>{fmtDate(inv.detected_at)}</span></div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" data-testid={`dup-keep-${inv.id}`} onClick={() => keep(g, inv)}
                        className="bg-green-700 hover:bg-green-800 text-white flex-1 gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Conservar esta
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        const r = await api.get(`/invoices/${inv.id}/file`, { responseType: "blob" });
                        window.open(URL.createObjectURL(r.data), "_blank");
                      }}>Ver</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
